import PropTypes from "prop-types";
import { Card, Divider, Skeleton } from "@mantine/core";
import CustomBadge from "../../ui/CustomBadge/CustomBadge";
import FetchFeedback from "../../ui/CustomFetchFeedback/CustomFetchFeedback";
import useBatchDetail from "../hooks/useBatchDetail";
import {
  BULK_UPLOAD_STATUS_MAP,
  BULK_UPLOAD_STATUS_LABEL,
} from "../constants/bulkUploadsConstants";
import {
  getDisplayValue,
  formatDateTimeToLocale,
} from "../../../utils/transactionutils";

function FieldRow({ label, children }) {
  return (
    <div className="flex flex-col flex-1">
      <div className="flex items-center justify-between flex-1 min-h-[52px] gap-4">
        <span className="text-sm text-gray-500 shrink-0">{label}</span>
        <span className="text-sm font-semibold text-gray-900 text-right">
          {children}
        </span>
      </div>
      <Divider color="gray.2" />
    </div>
  );
}

FieldRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};


const SKELETON_ROWS = 6;

function SkeletonRow() {
  return (
    <div>
      <div className="flex items-center justify-between py-3">
        <Skeleton height={14} width={100} radius="sm" />
        <Skeleton height={14} width={130} radius="sm" />
      </div>
      <Divider color="gray.2" />
    </div>
  );
}
function BatchDetailsCardSkeleton() {
  return (
    <Card radius="lg" className="p-5 sm:p-6 h-full">
      <Skeleton height={20} width={120} mb={16} radius="sm" />
      <Divider color="gray.2" mb={4} />
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <SkeletonRow key={index} />
      ))}
    </Card>
  );
}


export default function BatchDetailsCard({ id, isSuperAdmin = false }) {
  const {
    batchId,
    submittedBy,
    submittedOn,
    approvedBy,
    paymentChannel,
    status,
    merchantName,
    isLoading,
    isError,
    refetch,
  } = useBatchDetail(id, isSuperAdmin);

  if (isLoading) return <BatchDetailsCardSkeleton />;

  if (isError) {
    return (
      <Card
        radius="lg"
        className="p-5 sm:p-6 h-full flex items-center justify-center min-h-[300px]"
      >
        <FetchFeedback
          message="Failed to load batch details."
          onRetry={refetch}
          fullHeight={false}
          compact
        />
      </Card>
    );
  }


  return (
    <Card radius="lg" className="p-5 sm:p-6 h-full flex flex-col">
      <p className="text-sm font-semibold text-gray-900 mb-3">Batch Details</p>
      <Divider color="gray.2" />

      <div className="flex flex-col flex-1">
        <FieldRow label="Batch ID">
          <span className="text-primary-700 font-medium">
            {getDisplayValue(batchId)}
          </span>
        </FieldRow>

        {isSuperAdmin && (
          <FieldRow label="Merchant">{getDisplayValue(merchantName)}</FieldRow>
        )}

        <FieldRow label="Submitted By">{getDisplayValue(submittedBy)}</FieldRow>

        <FieldRow label="Submitted On">
          {formatDateTimeToLocale(submittedOn)}
        </FieldRow>

        <FieldRow label="Approved By">{getDisplayValue(approvedBy)}</FieldRow>

        <FieldRow label="Payment Channel">
          {paymentChannel ? (
            <CustomBadge color="primary" variant="light" size="sm">
              {paymentChannel}
            </CustomBadge>
          ) : (
            "-"
          )}
        </FieldRow>

        <FieldRow label="Status">
          {status ? (
            <CustomBadge
              color={BULK_UPLOAD_STATUS_MAP[status] ?? "gray"}
              variant="light"
              size="sm"
            >
              {BULK_UPLOAD_STATUS_LABEL[status] ?? status}
            </CustomBadge>
          ) : (
            "-"
          )}
        </FieldRow>
      </div>
    </Card>
  );
}

BatchDetailsCard.propTypes = {
  id: PropTypes.string,
  isSuperAdmin: PropTypes.bool,
};
